import { useState } from "react";
import type { MsofficeApiClient } from "../../api/types";
import { useSnackbar } from "../../../components/SnackbarProvider";
import type {
  ReminderWithAction,
  ExecuteActionResponse,
} from "@services/emailAutomationPipeline/types";
import type { UpdateReminderResponse } from "@services/shipments/reminders/types";
import { EventLogger } from "@shared/utils/eventLogger";

export interface DraftPreview {
  reminder: ReminderWithAction;
  draftMessageId: string;
  to?: string[];
  cc?: string[];
  subject?: string;
  attachments?: { attachmentId?: string; filename: string; contentType?: string; downloadUrl: string }[];
  missingAttachments?: { name: string }[];
}

interface UseShipmentActionsOptions {
  /** When true, send/resolve filter the reminder out. When false, mark as resolved in place. Default: true. */
  removeOnComplete?: boolean;
}

export function useShipmentActions(
  apiClient: MsofficeApiClient,
  setReminders: React.Dispatch<React.SetStateAction<ReminderWithAction[]>>,
  options?: UseShipmentActionsOptions,
) {
  const removeOnComplete = options?.removeOnComplete ?? true;
  const { showSnackbar } = useSnackbar();
  const [draftPreview, setDraftPreview] = useState<DraftPreview | null>(null);

  const draftPreviewAction = draftPreview
    ? {
        id: draftPreview.reminder.actionId,
        actionType: draftPreview.reminder.actionType,
        label: draftPreview.reminder.label ?? "",
        data: draftPreview.reminder.data,
        executionState: draftPreview.reminder.executionState,
        createdAt: draftPreview.reminder.actionCreatedAt,
        shipmentId: draftPreview.reminder.shipmentId ?? null,
      }
    : null;

  const handleExecute = async (reminder: ReminderWithAction) => {
    EventLogger.getInstance().track({
      name: "shipment_timeline_action_triggered",
      actionId: reminder.actionId,
      actionType: reminder.actionType,
      reminderId: reminder.reminderId,
      reminderType: reminder.reminderType,
      reminderName: reminder.reminderName,
      shipmentId: reminder.shipmentId ?? undefined,
    });
    try {
      const result = await apiClient.post<ExecuteActionResponse>(
        `/email-automation/actions/${encodeURIComponent(reminder.actionId)}/execute`,
      );
      setReminders((prev) =>
        prev.map((r) =>
          r.actionId === reminder.actionId
            ? { ...r, executionState: "RUN" }
            : r,
        ),
      );
      if (result.draftMessageId) {
        setDraftPreview({
          reminder,
          draftMessageId: result.draftMessageId,
          to: result.to,
          cc: result.cc,
          subject: result.subject,
          attachments: result.attachments,
          missingAttachments: result.missingAttachments,
        });
      }
    } catch {
      showSnackbar("Action execution failed", 5000, "error");
    }
  };

  const handleSend = async (actionId: string) => {
    if (draftPreview) {
      EventLogger.getInstance().track({
        name: "shipment_timeline_action_email_sent",
        actionId: draftPreview.reminder.actionId,
        actionType: draftPreview.reminder.actionType,
        reminderId: draftPreview.reminder.reminderId,
        reminderType: draftPreview.reminder.reminderType,
        reminderName: draftPreview.reminder.reminderName,
        shipmentId: draftPreview.reminder.shipmentId ?? undefined,
        emailTo: draftPreview.to,
        emailCc: draftPreview.cc,
        emailSubject: draftPreview.subject,
        emailBody: draftPreview.reminder.data.messageContent as string | undefined,
      });
    }
    try {
      await apiClient.post(
        `/email-automation/actions/${encodeURIComponent(actionId)}/send`,
      );
      if (removeOnComplete) {
        setReminders((prev) => prev.filter((r) => r.actionId !== actionId));
      } else {
        setReminders((prev) =>
          prev.map((r) =>
            r.actionId === actionId
              ? { ...r, executionState: "SENT", reminderStatus: "resolved" }
              : r,
          ),
        );
      }
      setDraftPreview(null);
      showSnackbar("Email sent", 3000, "success");
    } catch {
      showSnackbar("Failed to send email", 5000, "error");
    }
  };

  const handleAddAttachment = async (actionId: string, attachmentId: string) => {
    try {
      const result = await apiClient.post<{
        attachments: { attachmentId?: string; filename: string; contentType?: string; downloadUrl: string }[];
        missingAttachments: { name: string }[];
      }>(
        `/email-automation/actions/${encodeURIComponent(actionId)}/add-attachment`,
        { attachmentId },
      );
      setDraftPreview((prev) => {
        if (!prev) return null;
        const currentResolved = (prev.reminder.data.resolvedAttachments as Array<{ attachmentId: string; filename: string }>) || [];
        const newAtt = result.attachments.find((a) => a.attachmentId === attachmentId);
        return {
          ...prev,
          attachments: result.attachments,
          missingAttachments: result.missingAttachments,
          reminder: {
            ...prev.reminder,
            data: {
              ...prev.reminder.data,
              resolvedAttachments: [
                ...currentResolved,
                { attachmentId, filename: newAtt?.filename || "attachment" },
              ],
            },
          },
        };
      });
    } catch {
      showSnackbar("Failed to add attachment", 5000, "error");
    }
  };

  const handleRemoveAttachment = async (actionId: string, attachmentId: string) => {
    try {
      const result = await apiClient.post<{
        attachments: { attachmentId?: string; filename: string; contentType?: string; downloadUrl: string }[];
      }>(
        `/email-automation/actions/${encodeURIComponent(actionId)}/remove-attachment`,
        { attachmentId },
      );
      setDraftPreview((prev) => {
        if (!prev) return null;
        const currentResolved = (prev.reminder.data.resolvedAttachments as Array<{ attachmentId: string; filename: string }>) || [];
        return {
          ...prev,
          attachments: result.attachments,
          reminder: {
            ...prev.reminder,
            data: {
              ...prev.reminder.data,
              resolvedAttachments: currentResolved.filter((r) => r.attachmentId !== attachmentId),
            },
          },
        };
      });
    } catch {
      showSnackbar("Failed to remove attachment", 5000, "error");
    }
  };

  const handleResolve = async (reminder: ReminderWithAction) => {
    if (!reminder.reminderId) return;
    EventLogger.getInstance().track({
      name: "shipment_timeline_action_dismissed",
      actionId: reminder.actionId,
      actionType: reminder.actionType,
      reminderId: reminder.reminderId,
      reminderType: reminder.reminderType,
      shipmentId: reminder.shipmentId ?? undefined,
    });
    try {
      await apiClient.post<UpdateReminderResponse>(
        "/shipments/reminders/update-reminder",
        {
          reminderId: reminder.reminderId,
          resolve: true,
        },
      );
      if (removeOnComplete) {
        setReminders((prev) =>
          prev.filter((r) => r.reminderId !== reminder.reminderId),
        );
      } else {
        setReminders((prev) =>
          prev.map((r) =>
            r.reminderId === reminder.reminderId
              ? { ...r, reminderStatus: "resolved" }
              : r,
          ),
        );
      }
      showSnackbar("Reminder resolved", 3000, "success");
    } catch {
      showSnackbar("Failed to resolve reminder", 5000, "error");
    }
  };

  return {
    draftPreview,
    setDraftPreview,
    draftPreviewAction,
    handleExecute,
    handleSend,
    handleAddAttachment,
    handleRemoveAttachment,
    handleResolve,
  };
}
